import React, { useEffect, useRef, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { DataTableWithoutButtons } from "components/DataTable/DataTableWithoutButtons";
import { AfDataType } from "utils/TableColumnData";
import handleWheel from "utils/handleOnWheel";
import { ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { Img } from "components";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import { roles } from "utils/constant";
import { utils, write, writeFile } from "xlsx";
import { saveAs } from "file-saver";
import { Popover, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@heroui/react";
import PaymentOutstanding from "./PaymentOutstanding";
import PaymentStatement from "./PaymentStatement";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { AddCollection } from "./AddCollection";

// Compact select styles matching Credit Report
const pickupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 2px",
    minHeight: "32px",
    fontSize: "12px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "32px" }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "12px",
    padding: "4px 10px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({ ...base, borderRadius: "10px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)", zIndex: 9999 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "12px" }),
};

const CollectionReport: React.FC<any> = (props) => {
  const ADT = AfDataType;
  const tableRef = useRef(null);
  const statementRef = useRef<any>(null);

  const [invoiceTableData, setInvoiceTableData] = useState<any>([]);
  const [tableDetails, setTableDetails] = useState<any>([]);

  const [startDate, setStartDate] = useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));
  const [query, setQuery] = useState<any>({
    fromDate: moment().startOf("day"),
    toDate: moment().endOf("day"),
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fetchedData, setFetchedData] = useState([]);
  const [isDataAdded, setIsDataAdded] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerList, setCustomerList] = useState([]);

  const [searchIncrement, setSearchIncrement] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState<any>([]);

  const [totalCredit, setTotalCredit] = useState(0);
  const [totalTdsCredit, setTotalTdsCredit] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);

  const [invoiceWisePayment, setInvoiceWisePayment] = useState({});
  const [selectedSum, setSelectedSum] = useState<number | null>(null);
  const [showSum, setShowSum] = useState(false);
  const [selectedCellsSet, setSelectedCellsSet] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedCustomer || !selectedCustomer.value) {
      setSearchIncrement(0);
    }
		setSelectedInvoice([]);
  }, [selectedCustomer]);

  useQuery(["CustomersListApi"], () => ListCommanWay("/admin/customer/list", { isActive: true }, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt: any[] = [];
        data.data.data.forEach((p: any) => {
          opt.push({
            value: p.id,
            label: p.code + " - " + p.sh,
            customer: p,
          });
        });
        setCustomerList(opt);
      }
    },
  });

	useEffect(() => {
		console.log("Selected Invoices changed:", selectedInvoice);
	}, [selectedInvoice]);

  const handleOutstandingSlip = () => {
    if (!selectedCustomer?.value) {
      UseToast("Please select a customer first", "error");
      return;
    }
    if (statementRef.current?.handleOutstandingSlip) {
      statementRef.current.handleOutstandingSlip();
    } else {
      UseToast("Statement data is loading. Please wait...", "warning");
    }
  };

  const exportToExcel = () => {
    const table = tableRef.current;

    if (!table) {
      UseToast("No data available to export", "error");
      return;
    }

    const tableClone = table.cloneNode(true);

    // Remove elements with ignore-excel class if any
    const toIgnore = tableClone.querySelectorAll(".ignore-excel");
    toIgnore.forEach((el) => el.remove());

    // Convert table to worksheet
    const ws = utils.table_to_sheet(tableClone);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Collection Report");

    // Clean up cell properties
    Object.keys(ws).forEach((key) => {
      if (key.startsWith("!")) return;

      let cell = ws[key];
      if (cell && cell.h && typeof cell.h === "string") {
        cell.h = null;
      }
      if (cell && cell.l) {
        delete cell.l;
      }
    });

    // Generate Excel file
    const wbout = write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `Collection_Report_${moment().format("DD-MM-YYYY")}.xlsx`);
  };

  return (
    <div className="pb-2 px-2 font-inter min-h-screen">
      {/* Header Bar */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
            {/* Mode / Status Badge on Left */}
            <div className="flex-1"></div>

            {/* Centered Gradient Title */}
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              COLLECTION REPORT
            </h1>

            <div className="flex-1"></div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl shadow-slate-200/50">
          <div className="flex flex-wrap items-end gap-3">
            {/* Customer Filter */}
            <div className="min-w-[260px] flex-1 max-w-sm">
              <label className="block mb-1 text-[11px] font-medium text-slate-600">Select Customer</label>
              <Select
                styles={pickupSelectStyles}
                options={customerList}
                isClearable={true}
                onChange={(e: any) => {
                  setSelectedCustomer(e);
                }}
                value={selectedCustomer}
                placeholder="All Customers (Outstanding View)"
                menuPortalTarget={document.body}
              />
            </div>

            {/* Date Range */}
            <AfDateRangePicker setStartDate={setStartDate} setEndDate={setEndDate} label="Date Range" />

            {/* Action Buttons */}
            <Button
              size="sm"
              color="success"
              className="h-8 rounded-lg font-semibold"
              type="button"
              onPress={() => {
                setSearchIncrement(searchIncrement + 1);
              }}
              startContent={<MagnifyingGlassIcon className="w-4 h-4" />}
            >
              Search
            </Button>

            <Button
              size="sm"
              variant="flat"
              color="primary"
              className="h-8 rounded-lg font-semibold"
              startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
              onPress={exportToExcel}
            >
              Export
            </Button>

            {selectedInvoice && selectedInvoice.length > 0 && (
              <Button
                size="sm"
                variant="flat"
                color="default"
                className="h-8 rounded-lg font-semibold text-slate-600 hover:text-slate-800"
                type="button"
                onPress={() => setSelectedInvoice([])}
              >
                Clear Selection ({selectedInvoice.length})
              </Button>
            )}

            <Button
              size="sm"
              className={`h-8 rounded-lg font-semibold bg-indigo-600 text-white hover:bg-indigo-700 ${
                !selectedCustomer?.value ? "opacity-50 cursor-not-allowed" : ""
              }`}
              startContent={<PrinterIcon className="w-4 h-4" />}
              onPress={handleOutstandingSlip}
              disabled={!selectedCustomer?.value}
            >
              Outstanding Slip{selectedInvoice && selectedInvoice.length > 0 ? ` (${selectedInvoice.length} Selected)` : ""}
            </Button>

            <AddCollection invoiceData={""} tableData={invoiceTableData} selectedInvoice={selectedInvoice} />
          </div>
        </div>
      </div>
      <div className="">
        {selectedCustomer?.value ? (
          <PaymentStatement
            ref={statementRef}
            searchIncrement={searchIncrement}
            customerId={selectedCustomer?.value}
            customerData={selectedCustomer?.customer}
            selectedInvoice={selectedInvoice}
            setSelectedInvoice={setSelectedInvoice}
						setInvoiceTableData={setInvoiceTableData}
            tableRef={tableRef}
          />
        ) : (
          <PaymentOutstanding
            startDate={startDate}
            endDate={endDate}
            searchIncrement={searchIncrement}
            tableRef={tableRef}
          />
        )}
      </div>
    </div>
  );
};

export default CollectionReport;
